/* Cadence + skip math for the Routine calendar.
 *
 * Pure functions, no React. Client computes the upcoming-deliveries view
 * locally for v1 — the set of subs per user is small. When the backend
 * grows a server-side schedule endpoint, this module's `deliveriesOn`
 * gets swapped for an API call without touching the UI.
 */

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Local-tz YYYY-MM-DD for a Date. */
export function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function dayAbbrev(d) {
  return DAY_ABBR[d.getDay()];
}

/** Strip a Date down to its midnight in local tz. */
export function startOfDay(d) {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

/** Whole-day diff between two Dates (b - a). Negative if b is before a. */
export function daysBetween(a, b) {
  const A = startOfDay(a).getTime();
  const B = startOfDay(b).getTime();
  return Math.round((B - A) / 86_400_000);
}

/** Inclusive list of `count` consecutive dates starting from `start`. */
export function nextDays(start, count) {
  const base = startOfDay(start);
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(base);
    d.setDate(d.getDate() + i);
    return d;
  });
}

/**
 * Does this subscription's cadence fall on this date, ignoring skips?
 * Uses sub.cadence + sub.weeklyDay + sub.customDays + sub.anchorDate.
 */
export function subFiresOn(sub, date) {
  if (sub.status !== 'active') return false;
  const cad = sub.cadence;
  if (cad === 'Daily') return true;
  if (cad === 'Weekly') return dayAbbrev(date) === (sub.weeklyDay || 'Mon');
  if (cad === 'Custom') return !!(sub.customDays && sub.customDays[dayAbbrev(date)]);
  if (cad === 'Alt-day') {
    const anchor = sub.anchorDate ? new Date(sub.anchorDate) : new Date();
    const diff = Math.abs(daysBetween(anchor, date));
    return diff % 2 === 0;
  }
  return false;
}

/** Is this date on the subscription's skipped list? */
export function isSkipped(sub, date) {
  const iso = isoDate(date);
  return Array.isArray(sub.skippedDates) && sub.skippedDates.includes(iso);
}

/**
 * Deliveries scheduled on `date` across the supplied subs.
 * Returns one row per firing subscription, with `skipped: bool`.
 */
export function deliveriesOn(date, subs) {
  if (!Array.isArray(subs)) return [];
  return subs
    .filter((sub) => subFiresOn(sub, date))
    .map((sub) => ({
      subscriptionId: sub.id,
      productId: sub.productId,
      productName: sub.product,
      productEm: sub.productEm || '🛒',
      variantLabel: sub.variant,
      qty: sub.qty,
      branchName: sub.branchName,
      deliveryWindow: sub.window,
      pricePaise: sub.priceP,
      skipped: isSkipped(sub, date),
    }));
}

/** Count of NON-skipped deliveries on a given date. Drives chip badges. */
export function deliveryCountOn(date, subs) {
  return deliveriesOn(date, subs).filter((d) => !d.skipped).length;
}

/** Soonest upcoming delivery row from today onward (inclusive). Used by Home's next-delivery card. */
export function nextDelivery(subs, fromDate = new Date()) {
  const horizon = nextDays(fromDate, 30);
  for (const d of horizon) {
    const rows = deliveriesOn(d, subs).filter((r) => !r.skipped);
    if (rows.length) return { date: d, row: rows[0], count: rows.length };
  }
  return null;
}
